import { Injectable } from '@angular/core';
import { environment } from '@src/environments/environment';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  public static formatDateStringFromIsoToCustom(
    date: string,
    format: string = ''
  ) {
    const dateObject = new Date(date);
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(dateObject.getDate()).padStart(2, '0');
    const region = this.getRegion();

    if (!format && region === 'nam') {
      return `${month}/${day}/${year}`;
    } else if (!format && region === 'europe') {
      return `${day}/${month}/${year}`;
    }
    return format
      .replace('YYYY', String(year))
      .replace('MM', month)
      .replace('DD', day);
  }

  public static getUserName(): string {
    let username = 'UserNameNotKnown';
    if (this.isAtECLPlatform()) {
      const token = this.getUserToken();
      if (!token) return username;
      const decodedToken = jwtDecode<any>(token);
      if (decodedToken['first_name']) {
        username = decodedToken['first_name'];
      }
      return username;
    }
    const localStorage = window.localStorage;
    return localStorage['userName'] ?? username;
  }

  public static getUserEmail(): string {
    let useremail = 'UserEmailNotKnown';
    if (this.isAtECLPlatform()) {
      const token = this.getUserToken();
      if (!token) return useremail;
      const decodedToken = jwtDecode<any>(token);
      if (decodedToken['email']) {
        useremail = decodedToken['email'];
      }
      return useremail;
    }
    const localStorage = window.localStorage;
    return localStorage['userEmail'] ?? useremail;
  }

  static isAtECLPlatform(): boolean {
    const eclOrigin = environment.config.ECL_PLATFORM_URL;
    // Remove temporaryValidation after legacy eclp dns is deprecated and all legacy refs
    const temporaryValidation =
      document.referrer.includes('https://ecl-platform.uat.maersk.com/') ||
      document.referrer.includes('https://ecl-platform.maersk.com/');
    if (
      (document.referrer.includes(eclOrigin) || temporaryValidation) &&
      (window.localStorage['accessToken'] != null ||
        window.sessionStorage['accessToken'] != null)
    ) {
      return true;
    }
    return false;
  }

  public static getUserToken(): string | null {
    const sessionStorage = window.sessionStorage;

    if (
      sessionStorage['accessToken'] != null &&
      sessionStorage['accessToken'] != ''
    ) {
      return sessionStorage['accessToken']!;
    }

    const localStorage = window.localStorage;
    if (
      localStorage['accessToken'] != null &&
      localStorage['accessToken'] != ''
    ) {
      return localStorage['accessToken']!;
    }

    const tokenUrlSplit = window.location.href.split('?token=');
    if (tokenUrlSplit.length > 1) {
      let token = tokenUrlSplit[1];
      if (token.includes('&')) {
        token = token.split('&')[0];
      }
      return token;
    }

    return null;
  }

  public static requestDownloadFromParent(url: string) {
    const data = { destination: 'parent', type: 'downloadUrl', data: url };
    window.postMessage(data, window.location.origin);
  }

  public static downloadFileOnWeb(url: string) {
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public static downloadAFile(url: string) {
    if (this.isAtECLPlatform()) {
      this.downloadFileOnWeb(url);
    } else {
      this.requestDownloadFromParent(url);
    }
  }

  public static postDisplaySidePanel(isDisplaying: boolean) {
    const targetOrigin = environment.config.ECL_PLATFORM_URL;
    const data = {
      type: 'display_sidepanel',
      stack: 'angular',
      displaying: isDisplaying,
    };

    if (window.parent !== null) {
      window.parent.postMessage(data, targetOrigin);
    }
  }

  public static isPeriodOfTimeActive(
    startDate?: string,
    endDate?: string
  ): boolean {
    try {
      if (startDate == undefined || endDate == undefined) return false;
      const region = UtilsService.getRegion();
      const startDateObj = UtilsService.parseDateByRegion(startDate, region);
      const endDateObj = UtilsService.parseDateByRegion(endDate, region);
      if (!startDateObj || !endDateObj) return false;

      endDateObj.setHours(23, 59, 59);
      const startDateTime = startDateObj.getTime();
      const endDateTime = endDateObj.getTime();
      const currentDateTime = new Date().getTime();

      return startDateTime <= currentDateTime && currentDateTime <= endDateTime;
    } catch (e) {
      return false;
    }
  }

  public static formatDateToBeConsumed(date: string): string {
    try {
      if (!date || typeof date !== 'string') return date;

      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }

      const parts = date.split('/');
      if (parts.length !== 3) return date;

      const [first, second, third] = parts;
      let day: string, month: string, year: string;

      const region = this.getRegion()?.toLowerCase();

      if (region === 'nam' || region === 'eastus2') {
        [month, day, year] = [first, second, third];
      } else if (
        region === 'europe' ||
        region === 'eur' ||
        region === 'westeurope'
      ) {
        [day, month, year] = [first, second, third];
      } else {
        [year, month, day] = [first, second, third];
      }

      const pad = (n: string) => n.padStart(2, '0');
      return `${year}-${pad(month)}-${pad(day)}`;
    } catch (e) {
      console.error('Error parsing date:', e);
      return date;
    }
  }

  public static parseDateToISOString(date: string): string {
    try {
      const parsedDate = Date.parse(date);
      const formatedDate = new Date(parsedDate);
      const isoString = formatedDate.toISOString();
      return isoString;
    } catch (e) {
      return date;
    }
  }

  public static getRegion(): string {
    const region = environment?.region?.toLowerCase();
    const supportedRegions = ['nam', 'europe', 'eur', 'westeurope', 'eastus2'];

    if (supportedRegions.includes(region)) {
      return region;
    }
    return 'nam';
  }

  public static toISO(dateStr: string): string {
    if (!dateStr) return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    const parts = dateStr.split('/');
    if (parts.length !== 3) return '';

    const [first, second, third] = parts.map((p) => parseInt(p, 10));

    let day: number, month: number, year: number;
    const region = this.getRegion()?.toLowerCase();

    switch (region) {
      case 'nam':
      case 'eastus2':
        [month, day, year] = [first, second, third];
        break;

      case 'eur':
      case 'europe':
      case 'westeurope':
        [day, month, year] = [first, second, third];
        break;

      default:
        [year, month, day] = [first, second, third];
        break;
    }

    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toISOString().split('T')[0];
  }

  public static getDateFormatByRegion(region: string): string {
    region = region?.toLowerCase();
    switch (region) {
      case 'eur':
      case 'europe':
      case 'westeurope':
        return 'DD/MM/YYYY';

      case 'nam':
      case 'eastus2':
        return 'MM/DD/YYYY';

      default:
        return 'YYYY-MM-DD';
    }
  }

  public static getDatePlaceholderByRegion(region: string): string {
    region = region?.toLowerCase();
    switch (region) {
      case 'eur':
      case 'europe':
      case 'westeurope':
        return 'DD/MM/YYYY';

      case 'nam':
      case 'eastus2':
        return 'MM/DD/YYYY';

      default:
        return 'YYYY-MM-DD';
    }
  }

  public static formatDateForDisplay(date: string): string {
    if (!date || typeof date !== 'string') return date;

    const parts = date.split('-');
    if (parts.length !== 3) return date;

    const [year, month, day] = parts;
    const region = this.getRegion().toLowerCase();

    if (region === 'nam' || region === 'eastus2') {
      return `${month}/${day}/${year}`;
    } else if (
      region === 'europe' ||
      region === 'eur' ||
      region === 'westeurope'
    ) {
      return `${day}/${month}/${year}`;
    } else {
      return `${year}-${month}-${day}`;
    }
  }
  public static makeFilterTag(
    key: string,
    values: string[] | string,
    maxVisible = 2,
    maxLength = 30
  ): { display: string; tooltip?: string } {
    if (Array.isArray(values)) {
      const visible = values.slice(0, maxVisible);
      const remaining = values.length - maxVisible;
      const label = `${key}: ${visible.join(', ')}${
        remaining > 0 ? `, +${remaining}` : ''
      }`;
      const tooltip =
        remaining > 0 ? `${key}: ${values.join(', ')}` : undefined;
      return { display: label, tooltip };
    } else {
      if (values.length > maxLength) {
        return {
          display: `${key}: ${values.slice(0, maxLength)}…`,
          tooltip: `${key}: ${values}`,
        };
      }
      return { display: `${key}: ${values}` };
    }
  }

  public static parseDateByRegion(date: string, region: string): Date | null {
    if (!date || typeof date !== 'string') return null;

    const parts = date.split('/');
    if (parts.length !== 3) return null;

    let day: number, month: number, year: number;
    region = region.toLowerCase();

    if (region === 'nam' || region === 'eastus2') {
      // MM/DD/YYYY
      [month, day, year] = parts.map(Number);
    } else if (
      region === 'eur' ||
      region === 'europe' ||
      region === 'westeurope'
    ) {
      // DD/MM/YYYY
      [day, month, year] = parts.map(Number);
    } else {
      // Default: YYYY-MM-DD or fallback
      [year, month, day] = parts.map(Number);
    }

    return new Date(year, month - 1, day);
  }
}
